import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { Chip, Popover } from '@mui/material';
import { createLabelLocalizer } from 'helpers/misc';
import { formatTranslation, unsafeTranslationKey } from 'intl';
import { CommonTranslationKeys } from 'intl/keys';
import React, { ReactElement, useCallback, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useProjectTimezone } from 'store/project';
import { FilterControlElement } from '../filtersRenderer/components/FilterControlElement';
import { describeDateRange, describeSelection } from './model';
import { isDateRangeSharedFilter, type SectionSharedFilter, type SharedFilterState } from './sharedFilters';
import { filterChipSx, filterPopoverPaperSx } from './styles';

type SectionFilterChipProps = Readonly<{
  state: SharedFilterState;
  /** The filter narrows the charts: highlighted, cleared with its ×. */
  isActive: boolean;
  disabled: boolean;
  onChange: (shared: SectionSharedFilter, value: unknown) => void;
  onClear: (shared: SectionSharedFilter) => void;
}>;

/**
 * One filter of a dashboard section as a chip (PT-6804): its name, or what is
 * chosen in it. It opens the filter's own control; an active filter clears
 * with its × (nothing chosen, or the default date range).
 */
export function SectionFilterChip({ state, isActive, disabled, onChange, onClear }: SectionFilterChipProps): ReactElement {
  const intl = useIntl();
  const timezone = useProjectTimezone();
  const chipRef = useRef<HTMLDivElement>(null);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const { shared, filter, value } = state;
  const isDateRange = isDateRangeSharedFilter(shared);
  const name = formatTranslation(intl, unsafeTranslationKey(filter.labelId));

  const label = useMemo(() => {
    if (isDateRange) {
      return describeDateRange(value, timezone) || name;
    }
    return isActive ? `${name}: ${describeSelection(filter, value, createLabelLocalizer(intl, filter.labels))}` : name;
  }, [filter, intl, isActive, isDateRange, name, timezone, value]);

  // The popover hangs under the whole chip, whether its label or its arrow was clicked.
  const open = (): void => setAnchor(chipRef.current);
  const change = useCallback((next: unknown): void => onChange(shared, next), [onChange, shared]);
  const clear = useCallback((): void => onClear(shared), [onClear, shared]);

  return (
    <>
      <Chip
        ref={chipRef}
        label={label}
        icon={isDateRange ? <CalendarTodayOutlinedIcon /> : undefined}
        onClick={open}
        // An active filter clears with its ×; any other opens with its arrow.
        onDelete={isActive ? clear : open}
        deleteIcon={
          isActive ? (
            <CloseIcon aria-label={formatTranslation(intl, CommonTranslationKeys.clear)} />
          ) : (
            <KeyboardArrowDownIcon />
          )
        }
        disabled={disabled}
        variant='outlined'
        aria-haspopup='dialog'
        aria-expanded={Boolean(anchor)}
        sx={filterChipSx(isActive)}
        data-qa={`section-filter-${shared}`}
      />
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={(): void => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { sx: filterPopoverPaperSx, 'aria-label': name } }}
      >
        <FilterControlElement
          type={filter.type}
          filter={filter}
          localValue={value}
          localSetter={change}
          disabled={disabled}
          openOnMount
        />
      </Popover>
    </>
  );
}

export const MemoSectionFilterChip = React.memo(SectionFilterChip);
